import { useQuery } from "@tanstack/react-query";
import { FC } from "react";
import {
  Button,
  Flyout,
  FlyoutHeader,
  FlyoutTitle,
} from "@monolith-forensics/monolith-ui";

import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";

import {
  DetailItem,
  DetailLabel,
  DetailValue,
  FlyoutContainer,
  HeaderContent,
} from "./components/MonolithFlyoutComponents";
import UserApi from "@/api/users/users";
import UsersColumnDefs from "../Users/UsersColumnDefs";

interface UserFlyoutProps {
  show: boolean;
  onClose?: () => void;
  defaultInfo: any;
  onNext: () => void;
  onPrevious: () => void;
}

const MainDetails: FC<UserFlyoutProps["defaultInfo"]> = ({ defaultData }) => {
  return (
    <FlyoutContainer>
      <div className="event-details">
        {UsersColumnDefs.filter((col) => col.dataField !== "notes").map(
          (col) => {
            const key = col.dataField;
            const label = col.caption;
            const value = col?.render
              ? col.render(defaultData)
              : defaultData[key];

            return (
              <DetailItem key={key}>
                <DetailLabel>{label}</DetailLabel>
                <DetailValue>{value}</DetailValue>
              </DetailItem>
            );
          }
        )}
        <DetailItem>
          <DetailLabel>Notes</DetailLabel>
        </DetailItem>
        <DetailValue>
          {defaultData.notes || "No notes for this contact"}
        </DetailValue>
      </div>
    </FlyoutContainer>
  );
};

export const UserFlyout: React.FC<UserFlyoutProps> = ({
  show,
  onClose,
  defaultInfo,
  onNext,
  onPrevious,
}) => {
  const { data: itemData, isLoading } = useQuery({
    queryKey: ["users", "list", { user_id: defaultInfo.user_id }],
    queryFn: () =>
      UserApi.getUsers({
        user_id: defaultInfo.user_id,
      }),
    initialData: [defaultInfo],
    select: (res) => {
      return res[0];
    },
  });

  return (
    <Flyout open={show} onClose={() => onClose?.()}>
      {isLoading && <div>Loading...</div>}
      {!isLoading && (
        <>
          <FlyoutHeader>
            <FlyoutTitle>{itemData.full_name}</FlyoutTitle>
            <HeaderContent>
              <div>User</div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 10,
                  alignItems: "center",
                  marginLeft: "auto",
                }}
              >
                <Button
                  size="xxs"
                  variant="outlined"
                  onClick={() => onPrevious?.()}
                  style={{ padding: "2px 4px" }}
                >
                  <ChevronLeftIcon size={14} />
                </Button>
                <Button
                  size="xxs"
                  variant="outlined"
                  onClick={() => onNext?.()}
                  style={{ padding: "2px 4px" }}
                >
                  <ChevronRightIcon size={14} />
                </Button>
              </div>
            </HeaderContent>
          </FlyoutHeader>
          <MainDetails defaultData={itemData} />
        </>
      )}
    </Flyout>
  );
};
